import type { GridRowParams } from '@mui/x-data-grid';
import { useCallback, useState } from 'react';
import type { FC } from 'react';

import { Table } from '@components/Table';
import type { TableProps } from '@components/Table';
import { VrsTypeFormDialogContainer } from '@components/VrsTypeForm';
import { useVrsTypes } from '@hooks/api';
import { useColumnVisibilityModel } from '@hooks/useColumnVisibilityModel';
import { useTableFilterSort } from '@hooks/useTableFilterSort';

import * as classes from './VrsTypesTable.module.css';
import {
  columns,
  initialTableFilterSortArgs,
  columnVisibilityModelDesktop,
  columnVisibilityModelMobile,
} from './constants';

export interface VrsTypesTableProps {
  syncWithQueryParams?: boolean;
}

export const VrsTypesTable: FC<VrsTypesTableProps> = ({ syncWithQueryParams = false }) => {
  const [vrsTypeId, setVrsTypeId] = useState<string | undefined>(undefined);
  const tableFilterSort = useTableFilterSort(syncWithQueryParams)(initialTableFilterSortArgs);
  const { filters, page, size, sort } = tableFilterSort;
  const { data, error, isLoading, mutate } = useVrsTypes(
    {
      filters,
      page,
      size,
      sort,
    },
    {
      refreshInterval: 60000,
    },
  );
  const columnVisibilityModel = useColumnVisibilityModel(
    columnVisibilityModelMobile,
    columnVisibilityModelDesktop,
  );

  const onRowClick: TableProps['onRowClick'] = useCallback(
    (rowData: GridRowParams<{ id: string }>) => {
      setVrsTypeId(rowData.row.id);
    },
    [setVrsTypeId],
  );

  const onClose = useCallback(() => {
    setVrsTypeId(undefined);
    mutate();
  }, [setVrsTypeId, mutate]);

  return (
    <div className={classes.container}>
      <VrsTypeFormDialogContainer onClose={onClose} vrsTypeId={vrsTypeId} />
      <Table
        columnVisibilityModel={columnVisibilityModel}
        columns={columns}
        disableSelectionOnClick
        error={error as Error}
        loading={isLoading}
        localStorageSettingsKey="vrs-types-table-settings"
        onRowClick={onRowClick}
        rowCount={data?.totalElements}
        rows={data?.content}
        tableFilterSort={tableFilterSort}
      />
    </div>
  );
};
